import { ITEMS, Rarity, callingCanUse, type ItemDef, type RarityLevel } from './items.js';

export interface LootEntry {
  itemId: string;
  weight: number;
  qty: [min: number, max: number];
}
export interface LootTable {
  id: string;
  /** Number of rolls, inclusive range. Ignored when `fixed` is set. */
  rolls: [min: number, max: number];
  entries: LootEntry[];
  /**
   * A table that does not roll: every container on it holds exactly this, every time. The
   * tutorial crate uses it, because "open the crate, then use the stim" is only a lesson if the
   * stim is definitely in there.
   */
  fixed?: readonly RolledLoot[];
}

export const LOOT_TABLES: Record<string, LootTable> = {
  crate_common: {
    id: 'crate_common',
    rolls: [1, 3],
    entries: [
      { itemId: 'scrap_hull', weight: 30, qty: [2, 6] },
      { itemId: 'wire_bundle', weight: 22, qty: [1, 4] },
      { itemId: 'med_stim', weight: 22, qty: [1, 2] },
      { itemId: 'colonist_tag', weight: 8, qty: [1, 2] },
      { itemId: 'ember_glass', weight: 6, qty: [1, 2] },
      // A thin seam of open-issue armour in world crates, so the paperdoll has a source that is
      // not a 3% monster roll. Calling-locked Uncommon+ armour is drop-only.
      { itemId: 'arm_waker_hands_common', weight: 3, qty: [1, 1] },
      { itemId: 'arm_waker_feet_common', weight: 3, qty: [1, 1] },
      { itemId: 'arm_waker_shoulders_common', weight: 3, qty: [1, 1] },
    ],
  },
  crate_hull: {
    id: 'crate_hull',
    rolls: [2, 3],
    entries: [
      { itemId: 'ember_glass', weight: 24, qty: [1, 3] },
      { itemId: 'med_kit', weight: 14, qty: [1, 1] },
      { itemId: 'wire_bundle', weight: 14, qty: [1, 3] },
      { itemId: 'data_slate', weight: 10, qty: [1, 1] },
      { itemId: 'hymn_shard', weight: 6, qty: [1, 1] },
      { itemId: 'wpn_compact_blade', weight: 5, qty: [1, 1] },
      { itemId: 'wpn_long_vigil', weight: 5, qty: [1, 1] },
      { itemId: 'arm_waker_head_common', weight: 6, qty: [1, 1] },
      { itemId: 'arm_waker_chest_common', weight: 6, qty: [1, 1] },
      { itemId: 'arm_waker_legs_common', weight: 6, qty: [1, 1] },
    ],
  },
  crate_hymn: {
    id: 'crate_hymn',
    rolls: [1, 2],
    entries: [
      { itemId: 'hymn_shard', weight: 40, qty: [1, 2] },
      { itemId: 'data_slate', weight: 20, qty: [1, 1] },
      { itemId: 'ember_glass', weight: 15, qty: [1, 2] },
      { itemId: 'candle_relic', weight: 6, qty: [1, 1] },
      { itemId: 'choir_core', weight: 2, qty: [1, 1] },
    ],
  },
  hymn_drop_chorister: {
    id: 'hymn_drop_chorister',
    rolls: [1, 1],
    entries: [
      { itemId: 'scrap_hull', weight: 55, qty: [1, 3] },
      { itemId: 'hymn_shard', weight: 20, qty: [1, 1] },
      { itemId: 'wire_bundle', weight: 25, qty: [1, 2] },
    ],
  },
  hymn_drop_cantor: {
    id: 'hymn_drop_cantor',
    rolls: [1, 2],
    entries: [
      { itemId: 'ember_glass', weight: 40, qty: [1, 2] },
      { itemId: 'hymn_shard', weight: 30, qty: [1, 1] },
      { itemId: 'data_slate', weight: 15, qty: [1, 1] },
      { itemId: 'med_stim', weight: 20, qty: [1, 1] },
    ],
  },
  verger_drop: {
    id: 'verger_drop',
    rolls: [3, 4],
    entries: [
      { itemId: 'hymn_shard', weight: 40, qty: [1, 2] },
      { itemId: 'candle_relic', weight: 25, qty: [1, 1] },
      { itemId: 'data_slate', weight: 20, qty: [1, 2] },
      { itemId: 'choir_core', weight: 15, qty: [1, 1] },
    ],
  },
  hymn_drop_sexton: {
    id: 'hymn_drop_sexton',
    rolls: [2, 3],
    entries: [
      { itemId: 'hymn_shard', weight: 50, qty: [1, 2] },
      { itemId: 'ember_glass', weight: 25, qty: [1, 3] },
      { itemId: 'choir_core', weight: 5, qty: [1, 1] },
      { itemId: 'med_kit', weight: 20, qty: [1, 1] },
    ],
  },
  /** The Waking's crate. Fixed, not rolled: the next step of the tutorial is "use the stim". */
  yard_crate: {
    id: 'yard_crate',
    rolls: [1, 1],
    entries: [],
    fixed: [
      { itemId: 'med_stim', qty: 1 },
      { itemId: 'scrap_hull', qty: 2 },
    ],
  },
};

export interface RolledLoot {
  itemId: string;
  qty: number;
}

/**
 * Roll a table with an injected RNG (0 <= rng() < 1) so the server can seed per container
 * and tests are deterministic. Stacks of the same item are merged.
 */
export function rollLoot(tableId: string, rng: () => number = Math.random): RolledLoot[] {
  const table = LOOT_TABLES[tableId];
  if (!table) return [];
  if (table.fixed) return table.fixed.map((r) => ({ ...r }));
  const total = table.entries.reduce((n, e) => n + e.weight, 0);
  const nRolls = table.rolls[0] + Math.floor(rng() * (table.rolls[1] - table.rolls[0] + 1));
  const out = new Map<string, number>();
  for (let i = 0; i < nRolls; i++) {
    let r = rng() * total;
    for (const e of table.entries) {
      r -= e.weight;
      if (r < 0) {
        const qty = e.qty[0] + Math.floor(rng() * (e.qty[1] - e.qty[0] + 1));
        out.set(e.itemId, (out.get(e.itemId) ?? 0) + qty);
        break;
      }
    }
  }
  const rolled: RolledLoot[] = [];
  for (const [itemId, qty] of out) {
    const max = ITEMS[itemId]?.stack ?? 1;
    let left = qty;
    while (left > 0) {
      const take = Math.min(left, max);
      rolled.push({ itemId, qty: take });
      left -= take;
    }
  }
  return rolled;
}

/** Small deterministic RNG (mulberry32) for seeded container rolls. */
export function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ==================================================================================
// Equipment drops (M6)
//
// Separate from `LOOT_TABLES` on purpose. Those tables are the *materials* economy — scrap, shards,
// stims — and every Hymn rolls one. This is the *gear* economy: armour and class weapons, rolled
// once per Hymn death against a chance-and-rarity table, tuned to WoW's "you will remember where
// you were standing" cadence rather than to a steady drip. A Chorister drops gear 3% of the time
// and a Legendary comes off a Sexton about once in eighteen hundred kills.
// ==================================================================================

export type HymnDropKind = 'chorister' | 'cantor' | 'sexton' | 'verger';

export interface DropTier {
  /** Probability that *any* piece of gear drops from this kind. */
  chance: number;
  /** Relative rarity weights, indexed by `Rarity` (Common..Legendary). Any scale; normalised. */
  weights: readonly [number, number, number, number, number];
}

/**
 * Chance and rarity by Hymn kind. The absolute per-kill rate for a rarity is `chance × weight/Σw`:
 *
 *   kind        any     Common   Uncommon   Rare     Epic     Legendary
 *   chorister   3%      2.34%    0.57%      0.09%    —        —
 *   cantor      6%      3.30%    1.92%      0.69%    0.09%    —
 *   sexton      14%     3.08%    5.60%      4.13%    1.13%    0.056%
 *   verger      100%    —        —          70%      27%      3%
 *
 * Legendary exists only on Sexton and Verger, per the owner's brief, and a Verger is guaranteed
 * Rare or better — it is the raid's boss and the only reliable route to the top of the ladder.
 */
export const HYMN_DROP_TIERS: Record<HymnDropKind, DropTier> = {
  chorister: { chance: 0.03, weights: [780, 190, 30, 0, 0] },
  cantor: { chance: 0.06, weights: [550, 320, 115, 15, 0] },
  sexton: { chance: 0.14, weights: [220, 400, 295, 85, 0] },
  verger: { chance: 1, weights: [0, 0, 700, 300, 0] },
};

/**
 * The gear ladder stops at Epic for the level 1-10 slice: the Legendary rung is the dungeon's, and
 * `ITEMS` has no Legendary weapon or armour piece to hand out. `pickGear` walks *down* the ladder
 * when a rarity has no eligible item, so raising a Legendary weight here starts working the day
 * such an item exists — nothing else has to change.
 */

/**
 * How far above their own level a drop may be. One rung: a level 4 kill can hand you the level 5
 * piece you are about to grow into, but never the level 9 Epic, which would flatten the ladder the
 * zone exists to walk you up.
 */
export const DROP_LEVEL_HEADROOM = 1;

/**
 * TODO(auction-house): the two rules that make gear tradeable live here and nowhere else.
 *
 *  1. `CLASS_BIAS` — the fraction of gear rolls filtered to the killer's own Calling. The other
 *     40% deliberately drop *other* Callings' gear, which is useless to the finder and is exactly
 *     the supply an auction house needs. Raise this to 1 to turn the game into a solo loot game;
 *     lower it to widen the market.
 *  2. `BIND_ON_PICKUP` — currently false everywhere: nothing binds, so every drop is tradeable the
 *     day a market ships. If Legendaries are ever meant to be untradeable, gate it here (by rarity)
 *     rather than sprinkling checks through the inventory code.
 */
export const CLASS_BIAS = 0.6;
export const BIND_ON_PICKUP = false;

/** Gear a drop may produce: armour and weapons only, never materials or consumables. */
const isGear = (d: ItemDef): boolean => d.kind === 'armor' || d.kind === 'weapon';

/** rarity -> every gear item at that rarity. Built once; ITEMS never changes at runtime. */
const GEAR_BY_RARITY: ItemDef[][] = [[], [], [], [], []];
for (const d of Object.values(ITEMS)) {
  if (isGear(d)) GEAR_BY_RARITY[d.rarity]?.push(d);
}
for (const list of GEAR_BY_RARITY) list.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

function pickRarity(weights: DropTier['weights'], rng: () => number): RarityLevel | null {
  const total = weights.reduce((n, w) => n + w, 0);
  if (total <= 0) return null;
  let r = rng() * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i] ?? 0;
    if (r < 0) return i as RarityLevel;
  }
  return (weights.length - 1) as RarityLevel;
}

export interface GearDrop {
  itemId: string;
  rarity: RarityLevel;
}

/**
 * Pick one gear item of `rarity`, biased towards `calling` (0 = no Calling picked yet).
 *
 * With probability `CLASS_BIAS` the pool is narrowed to items restricted to that Calling; the rest
 * of the time it is everything at that rarity, other Callings' gear included. If the narrowed pool
 * is empty (no Calling-restricted gear exists at Common or Uncommon) it falls back to the open
 * pool, so a low roll always produces *something* rather than eating the drop.
 */
export function pickGear(
  rarity: RarityLevel,
  rng: () => number,
  calling = 0,
  level = 99,
): string | null {
  const ceiling = level + DROP_LEVEL_HEADROOM;
  // Walk down the ladder: a rarity with nothing the finder could ever wear (no Legendary gear
  // exists yet, and level 9 Epics are out of a level 2's reach) falls through to the rung below
  // rather than eating the drop.
  for (let r = rarity; r >= 0; r--) {
    const all = (GEAR_BY_RARITY[r] ?? []).filter((d) => (d.levelReq ?? 1) <= ceiling);
    if (all.length === 0) continue;
    // TODO(auction-house): this is the class-specific bias. See CLASS_BIAS above.
    let pool = all;
    if (calling > 0 && rng() < CLASS_BIAS) {
      const mine = all.filter((d) => d.calling === calling);
      if (mine.length > 0) pool = mine;
      else pool = all.filter((d) => callingCanUse(d, calling));
    }
    if (pool.length === 0) pool = all;
    const pick = pool[Math.floor(rng() * pool.length)];
    if (pick) return pick.id;
  }
  return null;
}

/**
 * Roll a monster's gear drop. Returns null far more often than not — that is the point.
 * `rng` is injected so the server can seed per kill and the tests are exact.
 *
 * `level` is the **killer's** level, not the monster's: it is what decides whether a piece is
 * something they can put on tonight, and a drop nobody can wear is a drop that did not happen.
 */
export function rollHymnDrop(
  kind: HymnDropKind,
  rng: () => number = Math.random,
  calling = 0,
  level = 99,
): GearDrop | null {
  const tier = HYMN_DROP_TIERS[kind];
  if (!tier) return null;
  if (rng() >= tier.chance) return null;
  const rarity = pickRarity(tier.weights, rng);
  if (rarity === null) return null;
  const itemId = pickGear(rarity, rng, calling, level);
  if (itemId === null) return null;
  return { itemId, rarity: ITEMS[itemId]?.rarity ?? rarity };
}

/**
 * Quest-chain reward roll, called by the quest system when a chain step is turned in.
 *
 * Rarity climbs with `chainIndex`: step 0 is a guaranteed Uncommon, and by the end of a long chain
 * Epic is the floor with a real shot at Legendary — a quest is a promise, so unlike a mob drop this
 * never returns null. `kind` narrows the pool to armour or to a weapon ('any' takes either).
 */
export function questRewardRoll(
  chainIndex: number,
  kind: 'armor' | 'weapon' | 'any',
  rng: () => number = Math.random,
  calling = 0,
  level = 99,
): GearDrop {
  const step = Math.max(0, Math.min(9, Math.floor(chainIndex)));
  // Weights slide up the ladder one step at a time: at step 0 it is all Uncommon, at step 9 the
  // mass sits on Epic with a 12% tail into Legendary.
  const weights: [number, number, number, number, number] = [
    Math.max(0, 40 - step * 10),
    Math.max(0, 60 - step * 4),
    Math.min(60, 10 + step * 7),
    Math.max(0, step * 6 - 6),
    Math.max(0, step * 2 - 6),
  ];
  const rarity = pickRarity(weights, rng) ?? Rarity.Uncommon;
  const ceiling = level + DROP_LEVEL_HEADROOM;
  const wanted = (d: ItemDef): boolean =>
    (kind === 'any' || d.kind === kind) && (d.levelReq ?? 1) <= ceiling;
  for (let r = rarity; r >= 0; r--) {
    const pool = (GEAR_BY_RARITY[r] ?? []).filter(wanted);
    if (pool.length === 0) continue;
    // Same bias as a mob drop, so a quest reward is usually — not always — for your Calling.
    // TODO(auction-house): see CLASS_BIAS.
    let narrowed = pool;
    if (calling > 0 && rng() < CLASS_BIAS) {
      const mine = pool.filter((d) => d.calling === calling);
      if (mine.length > 0) narrowed = mine;
    }
    const pick = narrowed[Math.floor(rng() * narrowed.length)];
    if (pick) return { itemId: pick.id, rarity: pick.rarity };
  }
  return { itemId: 'scrap_hull', rarity: Rarity.Common };
}
